import { useRef, useState, useCallback } from "react";
import { T } from "@/locales/tr";

/**
 * Slider Avant / Après — fait glisser pour comparer.
 * Usage : <BeforeAfter beforeSrc="..." afterSrc="..." label={T("Présidentiel")} />
 */
export default function BeforeAfter({ beforeSrc, afterSrc, label, title }) {
  const [pos, setPos] = useState(50); // pourcentage de la position du curseur
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const updateFromEvent = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromEvent(cx);
  }, [updateFromEvent]);

  const onUp = useCallback(() => {
    dragging.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("mouseup", onUp);
    window.removeEventListener("touchend", onUp);
  }, [onMove]);

  const onDown = (e) => {
    dragging.current = true;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromEvent(cx);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
  };

  return (
    <div data-testid="before-after" className="papicoo-card overflow-hidden">
      {title && (
        <div className="px-4 pt-4 pb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{label || "Style"}</div>
          <div className="font-display text-lg text-[color:var(--papicoo-ink)] mt-1">{title}</div>
        </div>
      )}
      <div
        ref={wrapRef}
        onMouseDown={onDown}
        onTouchStart={onDown}
        className="relative w-full aspect-[4/5] select-none touch-none cursor-ew-resize bg-black"
      >
        <img src={afterSrc} alt={T("Après retouche Papicoo")} className="absolute inset-0 w-full h-full object-cover" draggable={false} loading="lazy" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={beforeSrc} alt={T("Avant retouche")} className="absolute inset-0 h-full object-cover" style={{ width: `${100 / (pos / 100)}%`, maxWidth: "none" }} draggable={false} loading="lazy" />
        </div>
        <div className="absolute top-3 left-3 px-2 py-0.5 bg-black/60 text-white text-[10px] uppercase tracking-wider rounded-full">{T("Avant")}</div>
        <div className="absolute top-3 right-3 px-2 py-0.5 bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)] text-[10px] uppercase tracking-wider rounded-full">{T("Après · Papicoo")}</div>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }} />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-xl flex items-center justify-center pointer-events-none"
          style={{ left: `${pos}%`, transform: "translate(-50%, -50%)" }}
        >
          <span className="text-[color:var(--papicoo-ink)] text-lg leading-none">⇆</span>
        </div>
      </div>
    </div>
  );
}
